<template>
    <div v-if="countries && genders && documentTypes">
        <template v-if="!formSaved">
            <h3>{{ $t('EXAMINATION_DATA_FORM_1_PART_1') }} {{ $t('EXAMINATION_DATA_FORM_1_PART_2') }}</h3>
            <div class="row mt-3">
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        maxlength="50"
                        name="firstName"
                        :placeholder="$t('FIRST_NAME')"
                        v-model="examinationData.firstName"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('firstName') }"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        maxlength="50"
                        name="lastName"
                        :placeholder="$t('LAST_NAME')"
                        v-model="examinationData.lastName"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('lastName') }"
                        type="text"
                    />
                </div>
                <div :class="[documentIsPESEL ? 'col-xs-12 col-sm-6' : 'col-xs-6 col-sm-3 pr-1', 'mb-4']">
                    <el-select v-model="documentType">
                        <el-option
                            v-for="documentType in documentTypes"
                            :key="documentType.id"
                            :label="$t(documentType.name)"
                            :value="documentType.name"
                        />
                    </el-select>
                </div>
                <div v-if="!documentIsPESEL" class="col-xs-6 col-sm-3 pl-1 mb-4">
                    <el-select
                        name="documentCountry"
                        v-model="documentCountry"
                        :placeholder="$t('DOCUMENT_COUNTRY')"
                        v-validate="'required'"
                        :class="{ 'invalid-date': formSubmitted && errors.has('documentCountry') }"
                    >
                        <el-option
                            v-for="item in countries"
                            :key="item.code"
                            :label="`${item.name} (${item.nativeName})`"
                            :value="item.code"
                        >
                        </el-option>
                    </el-select>
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="documentNumber"
                        :class="{ invalid: formSubmitted && errors.has('documentNumber') }"
                        :placeholder="$t('IDENTIFICATION_DOCUMENT_NUMBER')"
                        v-model="documentNumber"
                        v-validate="documentIsPESEL ? 'required|pesel' : 'required'"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <el-select
                        name="gender"
                        v-model="examinationData.gender"
                        :placeholder="$t('GENDER')"
                        v-validate="'required|female'"
                        :class="{ 'invalid-date': formSubmitted && errors.has('gender') }"
                        :disabled="documentIsPESEL && peselIsValid"
                    >
                        <el-option
                            v-for="gender in genders"
                            :key="gender.id"
                            :label="$t(gender.name)"
                            :value="gender.name"
                        />
                    </el-select>
                </div>
            </div>
            <div class="row mt-3">
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="street"
                        :placeholder="$t('STREET')"
                        v-model="examinationData.street"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('street') }"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="buildingNumber"
                        :placeholder="$t('BUILDING_NR')"
                        v-model="examinationData.buildingNumber"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('buildingNumber') }"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="flatNumber"
                        :placeholder="$t('LOCAL_NR')"
                        v-model="examinationData.flatNumber"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="city"
                        :placeholder="$t('CITY')"
                        v-model="examinationData.city"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('city') }"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <input
                        class="text-input -white-input-border"
                        name="zipCode"
                        :placeholder="$t('POSTAL_CODE')"
                        v-model="examinationData.zipCode"
                        v-validate="'required'"
                        :class="{ invalid: formSubmitted && errors.has('zipCode') }"
                        type="text"
                    />
                </div>
                <div class="col-xs-12 col-sm-6 mb-4">
                    <el-select
                        class="text-input -white-input-border"
                        :placeholder="$t('COUNTRY')"
                        filterable
                        :filter-method="filterOptions"
                        v-model="examinationData.country"
                    >
                        <el-option
                            v-for="item in filteredCountries"
                            :key="item.code"
                            :label="`${item.name} (${item.nativeName})`"
                            :value="item.code"
                        >
                        </el-option>
                    </el-select>
                </div>
            </div>
            <div class="row col-xs-12 text-center">
                <button @click="$emit('hideForm')" class="w160 mr-3 app-btn app-btn--small app-btn-default">
                    {{ $t('CANCEL') }}
                </button>
                <button @click="validateExaminationData" class="w160 app-btn app-btn--small app-btn-primary">
                    {{ $t('CONFIRM') }}
                </button>
            </div>
        </template>
        <template v-else>
            <h3>
                {{ $t('EXAMINATION_DATA_FORM_2_PART_1') }} {{ $t('EXAMINATION_DATA_FORM_2_PART_2') }}
                {{ $t('EXAMINATION_DATA_FORM_2_PART_3') }} {{ $t('EXAMINATION_DATA_FORM_2_PART_4') }}
            </h3>
            <div class="row col-xs-12 text-center">
                <button @click="closeForm" class="w160 app-btn app-btn--small app-btn-primary">
                    {{ $t('CLOSE') }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import {
    alphabeticSortByName,
    calculateBirthdayFromPesel,
    calculateGenderFromPesel,
    validatePesel,
} from '../../../utils';

const PESEL = 'PESEL';
const PL_DOCUMENT = 'PL_DOCUMENT';

export default {
    props: ['examinationId'],
    data() {
        return {
            formSubmitted: false,
            formSaved: false,
            documentCountry: '',
            documentType: this.$store.state.user.units[0].territory.country === 'PL' ? PESEL : PL_DOCUMENT,
            documentNumber: '',
            examinationData: {
                firstName: '',
                lastName: '',
                gender: 'FEMALE',
                street: '',
                buildingNumber: '',
                flatNumber: '',
                zipCode: '',
                city: '',
                country: 'PL',
            },
            filteredCountries: null,
        };
    },
    computed: {
        datePickerOptions() {
            const todayMinus18years = this.$moment().subtract(18, 'years');
            return {
                disabledDate: date => date > todayMinus18years,
            };
        },
        documentIsPESEL() {
            return this.documentType === PESEL;
        },
        peselIsValid() {
            return validatePesel(this.documentNumber);
        },
    },
    asyncComputed: {
        countries() {
            return this.$http
                .get('/user/public/registration/countries')
                .then(({ data }) => data.sort(alphabeticSortByName));
        },
        filteredCountries() {
            this.filteredCountries = this.countries;
        },
        genders() {
            return this.$http.get('/user/api/profile/genders').then(r => r.data);
        },
        documentTypes() {
            return this.$http.get('/user/api/profile/documentTypes').then(r => [{ id: 0, name: PESEL }, ...r.data]);
        },
    },
    watch: {
        documentType: 'setBirthDateAndGender',
        documentNumber: 'setBirthDateAndGender',
    },
    methods: {
        setBirthDateAndGender() {
            if (this.documentIsPESEL && this.peselIsValid) {
                this.examinationData.gender = calculateGenderFromPesel(this.documentNumber);
            }
        },
        validateExaminationData() {
            this.formSubmitted = true;
            this.$validator.validateAll().then(result => result && this.sendExaminationData());
        },
        sendExaminationData() {
            const examinationData = { ...this.examinationData };
            if (this.documentIsPESEL) {
                examinationData.pesel = this.documentNumber;
            } else {
                examinationData.documentType = this.documentType;
                examinationData.documentNumber = this.documentNumber;
                examinationData.documentCountry = this.documentCountry;
            }
            this.$http
                .post(`/examination/api/examination/${this.examinationId}/fillPatientProData`, examinationData)
                .then(() => (this.formSaved = true));
        },
        closeForm() {
            this.$emit('hideForm');
            this.$emit('reloadExaminations');
        },
        filterOptions(filterValue) {
            let filteredCountries = this.countries.filter(country => {
                return country.name.toLowerCase().startsWith(filterValue.toLowerCase());
            });

            if (filterValue === null) {
                this.filteredCountries = this.countries;
            } else {
                this.filteredCountries = filteredCountries;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.invalid {
    border: 2px solid red;
}
</style>



// WEBPACK FOOTER //
// examination-data-form.vue?01a1cf45